import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

import { getConfigWithToken } from "../config/config";
import { getSuperAdminUsers } from "../config/url";
import classes from "./SuperAdminList.module.css";

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 500;

const SuperAdminUsers = () => {
	const navigate = useNavigate();
	const token = useSelector((state) => state?.user?.user?.token) || localStorage.getItem("user_id");

	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [rows, setRows] = useState([]);
	const [search, setSearch] = useState("");
	const [totalItems, setTotalItems] = useState(0);
	const [totalPages, setTotalPages] = useState(1);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
			},
		}),
		[token],
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [search]);

	const fetchRows = useCallback(async () => {
		setIsLoading(true);
		setHasError(false);
		try {
			const response = await axios.get(getSuperAdminUsers, {
				...requestConfig,
				params: {
					limit: DEFAULT_PAGE_SIZE,
					page,
					...(debouncedSearch ? { search: debouncedSearch } : {}),
				},
			});
			const pagination = response?.data?.pagination || {};
			setRows(Array.isArray(response?.data?.data) ? response.data.data : []);
			setTotalItems(pagination.total || 0);
			setTotalPages(Math.max(1, pagination.totalPages || 1));
		} catch {
			setHasError(true);
			setRows([]);
			setTotalItems(0);
			setTotalPages(1);
		} finally {
			setIsLoading(false);
		}
	}, [debouncedSearch, page, requestConfig]);

	useEffect(() => {
		fetchRows();
	}, [fetchRows]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	return (
		<div className={classes.page}>
			<div className={classes.headerRow}>
				<h1 className={classes.title}>Users</h1>
				<p className={classes.subtitle}>Users linked to an organization.</p>
			</div>

			<div className={classes.toolbar}>
				<label className={classes.searchWrap}>
					<Search size={16} strokeWidth={1.75} />
					<input
						className={classes.searchInput}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Search users"
						value={search}
					/>
				</label>
			</div>

			{hasError ? (
				<div className={classes.errorBox}>
					<p>Users could not be loaded.</p>
					<button className={classes.retryButton} onClick={fetchRows} type="button">
						Retry
					</button>
				</div>
			) : (
				<>
					<div className={classes.tableWrap}>
						<table className={classes.table}>
							<thead>
								<tr>
									<th>Name</th>
									<th>Email</th>
									<th>Account Type</th>
									<th>Role</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{isLoading ? (
									<tr>
										<td className={classes.empty} colSpan={5}>
											Loading...
										</td>
									</tr>
								) : rows.length === 0 ? (
									<tr>
										<td className={classes.empty} colSpan={5}>
											No users found.
										</td>
									</tr>
								) : (
									rows.map((row) => (
										<tr
											className={classes.row}
											key={`${row.accountType}-${row.id}`}
											onClick={() => navigate(`/users/${row.accountType}/${row.id}`)}>
											<td>{row.name}</td>
											<td>{row.email || "-"}</td>
											<td>
												<span
													className={`${classes.badge} ${
														row.accountType === "admin" ? classes.badgeAdmin : classes.badgeUser
													}`}>
													{row.accountType === "admin" ? "Admin" : "User"}
												</span>
											</td>
											<td>{row.role || "-"}</td>
											<td>
												<span
													className={`${classes.badge} ${
														row.isActive ? classes.badgeActive : classes.badgeInactive
													}`}>
													{row.isActive ? "Active" : "Inactive"}
												</span>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
					<div className={classes.pagination}>
						<span>
							Page {page} of {totalPages} ({totalItems} total)
						</span>
						<button
							className={classes.pageButton}
							disabled={page <= 1}
							onClick={() => setPage((current) => current - 1)}
							type="button">
							<ChevronLeft size={16} />
						</button>
						<button
							className={classes.pageButton}
							disabled={page >= totalPages}
							onClick={() => setPage((current) => current + 1)}
							type="button">
							<ChevronRight size={16} />
						</button>
					</div>
				</>
			)}
		</div>
	);
};

export default SuperAdminUsers;
